import { connect, useDispatch } from 'react-redux';

import {
  getExcludedRanges,
  getSelectedExcludedRanges,
  getTotalColumns,
  getTotalRows
} from '../../_selectors';

import { cancelTableSelection, updateExcludedRanges } from '../../_actions';

import { Button, Input, Typography } from 'components';
import { TrashIcon, UndoIcon } from 'lib/svgs/Icons';
import { useTranslations } from 'next-intl';
import { ChangeEvent, FC, MouseEvent, useEffect, useState } from 'react';
import { cellToString, rangeToString, stringToCell } from '../../_utils';
import { Range } from '../Body';

type RangeEdit = {
  start: string;
  end: string;
  remove: boolean;
  startError: boolean;
  endError: boolean;
};

const initialState = (selectedExcludedRanges: Range[]) =>
  selectedExcludedRanges.map(({ start, end }) => ({
    start: cellToString(start),
    end: cellToString(end),
    remove: false
  })) as RangeEdit[];

interface IEditExcludedPopup {
  selectedExcludedRanges: Range[];
  excludedRanges: Range[];
  totalRows: number;
  totalColumns: number;
  cancelTableSelection: () => void;
  updateExcludedRanges: (ranges: Range[]) => void;
}

const EditExcludedPopup: FC<IEditExcludedPopup> = ({
  selectedExcludedRanges,
  totalRows,
  totalColumns,
  excludedRanges
}) => {
  const t = useTranslations();

  const dispatch = useDispatch();

  const [excluded, setExcluded] = useState(initialState(selectedExcludedRanges));

  const updateRange = (index: number, updatedExcludedRange: RangeEdit) =>
    setExcluded((oldState) =>
      oldState.map((excludedValue, i) => {
        if (i == index) return updatedExcludedRange;

        return excludedValue;
      })
    );

  const handleUndo = (range: RangeEdit, index: number) => (e: MouseEvent<HTMLButtonElement>) => {
    e.nativeEvent.stopImmediatePropagation();
    updateRange(index, { ...range, remove: false });
  };

  const handleTrash = (range: RangeEdit, index: number) => (e: MouseEvent<HTMLButtonElement>) => {
    e.nativeEvent.stopImmediatePropagation();
    updateRange(index, { ...range, remove: true });
  };

  const handleStart = (range: RangeEdit, index: number) => (e: ChangeEvent<HTMLInputElement>) =>
    updateRange(index, {
      ...range,
      start: String(e.target.value).toLocaleUpperCase(),
      endError: false
    });

  const handleEnd = (range: RangeEdit, index: number) => (e: ChangeEvent<HTMLInputElement>) =>
    updateRange(index, {
      ...range,
      end: String(e.target.value).toLocaleUpperCase(),
      endError: false
    });

  const handleApplyChanges = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();

    const selectedRangesIndexes = selectedExcludedRanges.map((r) => r.originalIndex);

    const nonSelectedExcludedRanges = excludedRanges.filter(
      (_, index) => !selectedRangesIndexes.includes(index)
    );

    const updatedExcludedRanges = excluded
      .filter((e) => !e.remove)
      .map(({ start, end }) => ({
        start: stringToCell(start),
        end: stringToCell(end)
      }));

    dispatch(updateExcludedRanges([...nonSelectedExcludedRanges, ...updatedExcludedRanges]));
    dispatch(cancelTableSelection());
  };

  const handleCancel = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    dispatch(cancelTableSelection());
  };

  const handleStopPropagation = (e: MouseEvent<HTMLDivElement>) => e.stopPropagation();

  let excludedHasErrors = false;

  useEffect(() => {
    setExcluded(initialState(selectedExcludedRanges));
  }, [selectedExcludedRanges]);

  if (selectedExcludedRanges.length !== excluded.length) return null;

  return (
    <div onClick={handleStopPropagation}>
      <Typography variant="regular1" className="text-tertiary-100">
        {t('ordering_form.edit_exclusion.header', { count: excluded.length })}
      </Typography>
      <div className="mt-4">
        <Typography variant="light4" className="text-quaternary-300">
          {t('ordering_form.edit_exclusion.explanation', { count: excluded.length })}
        </Typography>
      </div>
      <ul className="ordering_form__table__popup__edit_excluded">
        {excluded.map((range, index) => {
          const excludedRange = selectedExcludedRanges[index];

          if (range.remove)
            return (
              <li
                className="flex items-center justify-between"
                key={`edit-exclusion-${rangeToString(excludedRange, totalRows, totalColumns, t)}`}
              >
                <span>
                  <Typography variant="light3">
                    {t('ordering_form.edit_exclusion.removing')}
                  </Typography>
                  <Typography variant="light3" className="ml-4 text-tertiary-100">
                    {rangeToString(excludedRange, totalRows, totalColumns, t)}
                  </Typography>
                </span>
                <Button
                  variant="icon-btn"
                  className="h-10 w-10"
                  color="secondary"
                  onClick={handleUndo(range, index)}
                >
                  <UndoIcon />
                </Button>
              </li>
            );

          const startHasErrors = !stringToCell(range.start),
            endHasErrors = !stringToCell(range.end);

          excludedHasErrors = excludedHasErrors || startHasErrors || endHasErrors;

          return (
            <li
              key={`edit-exclusion-${rangeToString(excludedRange, totalRows, totalColumns, t)}`}
              className="flex gap-4"
            >
              <span className="label_range">
                <Typography variant="medium4">
                  {`${rangeToString(excludedRange, totalRows, totalColumns, t)}`}
                </Typography>
              </span>
              <div className="w-16">
                <Input
                  id={`${rangeToString(excludedRange, totalRows, totalColumns, t)}-start`}
                  type="text"
                  value={range.start}
                  onChange={handleStart(range, index)}
                  className="w-16"
                />
              </div>
              <div className="w-16">
                <Input
                  id={`${rangeToString(excludedRange, totalRows, totalColumns, t)}-end`}
                  type="text"
                  value={range.end}
                  error={endHasErrors}
                  onChange={handleEnd(range, index)}
                />
              </div>
              <Button
                variant="icon-btn"
                className="h-10 w-10"
                color="secondary"
                onClick={handleTrash(range, index)}
              >
                <TrashIcon />
              </Button>
            </li>
          );
        })}
      </ul>
      <div className="mb-2">
        {excludedHasErrors && (
          <Typography variant="light3" className="ordering_form__table__popup__warning">
            <span className="ticon-warning mr-2 text-xs" />
            {t('ordering_form.please_enter_valid_cell')}
          </Typography>
        )}
      </div>
      <div className="flex gap-4">
        <Button variant="contained" size="medium" color="secondary" onClick={handleApplyChanges}>
          {t('ordering_form.edit_exclusion.apply')}
        </Button>
        <Button variant="text" onClick={handleCancel}>
          {t('ordering_form.cancel')}
        </Button>
      </div>
    </div>
  );
};

export default connect(
  (state) => ({
    selectedExcludedRanges: getSelectedExcludedRanges(state),
    excludedRanges: getExcludedRanges(state),
    totalRows: getTotalRows(state),
    totalColumns: getTotalColumns(state)
  }),
  { cancelTableSelection, updateExcludedRanges }
)(EditExcludedPopup);
